import { useEffect, useState } from "react";

import RingVolumeIcon from "@mui/icons-material/RingVolume";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { neutral } from "app/themes/colors";
import { formatPhoneNumber } from "utils/common";

import { lastTenDigits } from "../phone-match.util";
import useIncomingCallPatients from "../use-incoming-call-patients.hook";
import { formatCallerIdNumber } from "./caller-id-select.component";
import {
  PLUM_600,
  callCardSx,
  e911CardTextButtonSx
} from "./dialstack-softphone-widget.styles";
import SoftphonePatientRow from "./softphone-patient-row.component";
import {
  type DeskPhoneDial,
  useDialstackSoftphone
} from "./use-dialstack-softphone.hook";

export const DESK_PHONE_WAITING_TITLE = "Pick up your desk phone";
export const DESK_PHONE_NO_ANSWER_TITLE = "Your desk phone wasn't answered";
export const DESK_PHONE_HANDSET_HINT =
  "Mute, hold, transfer and hang up on the handset.";
export const USE_COMPUTER_LABEL = "Use computer instead";
export const CALL_FROM_COMPUTER_LABEL = "Call from computer";

// Front-desk users are often 50+: nothing under 12px, the fact they look for
// first (who the call is with) biggest.
const titleSx = { fontSize: 14, fontWeight: 600 };
const bodySx = { fontSize: 13, color: neutral[800] };
const captionSx = { fontSize: 12, color: neutral[800] };
// The one action the user needs next is outlined; "Dismiss" stays quiet.
const primaryActionSx = {
  alignSelf: "flex-start",
  mt: 0.5,
  textTransform: "none",
  fontSize: 13,
  color: PLUM_600,
  borderColor: PLUM_600
};

const pad = (value: number) => String(value).padStart(2, "0");
export const formatElapsed = (ms: number) => {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  return `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;
};

// Ticks once a second while the call is up, like the SDK's own call timer.
export function useElapsed(since: number | null, until: number | null) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (since === null || until !== null) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [since, until]);
  if (since === null) return null;
  return formatElapsed((until ?? now) - since);
}

// The card sits in the widget's polite live region, which announces the phase
// text on change; the ticking clock is kept out of that so a screen reader is
// not read the time every second.
function Elapsed({ value }: { value: string | null }) {
  return value ? <span aria-hidden> · {value}</span> : null;
}

function whoLabel(dial: DeskPhoneDial) {
  return dial.patient?.name ?? formatPhoneNumber(dial.destination);
}

// Narrates a click-to-call the user is taking on their desk phone (ENG-2183).
// The SDK's cards cover only legs the browser carries; this one follows the
// handset call through the backend's webhook pushes. Nothing here can end
// the call — the platform has no hang-up API — so the copy says where to.
export default function DeskPhoneCallCard() {
  const {
    deskPhoneDial,
    dismissDeskPhoneDial,
    moveDeskPhoneDialToComputer,
    callerIds
  } = useDialstackSoftphone();
  const patientIds = deskPhoneDial?.patient ? [deskPhoneDial.patient.id] : [];
  const { patients } = useIncomingCallPatients(patientIds);
  const elapsed = useElapsed(
    deskPhoneDial?.answeredAt ?? null,
    deskPhoneDial?.endedAt ?? null
  );
  if (!deskPhoneDial) return null;

  const callerNumber = callerIds.find(
    (number) =>
      deskPhoneDial.callerNumber !== null &&
      lastTenDigits(number.phone_number) === deskPhoneDial.callerNumber
  );
  const who = whoLabel(deskPhoneDial);

  return (
    <Stack data-testid="desk-phone-call-card" spacing={0.5} sx={callCardSx}>
      {deskPhoneDial.phase === "waiting" && (
        <>
          <Stack direction="row" alignItems="center" spacing={1}>
            <RingVolumeIcon
              aria-hidden
              sx={{ fontSize: 18, color: PLUM_600 }}
            />
            <Typography sx={titleSx}>{DESK_PHONE_WAITING_TITLE}</Typography>
          </Stack>
          <Typography sx={bodySx}>
            Suno dials {who} as soon as you pick up. To stop, just don't answer.
          </Typography>
          {callerNumber && (
            <Typography sx={captionSx}>
              Calling from {formatCallerIdNumber(callerNumber.phone_number)}
            </Typography>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={moveDeskPhoneDialToComputer}
            sx={primaryActionSx}
          >
            {USE_COMPUTER_LABEL}
          </Button>
        </>
      )}
      {deskPhoneDial.phase === "answered" && (
        <>
          <Typography sx={captionSx}>
            On your desk phone
            <Elapsed value={elapsed} />
          </Typography>
          {patients.length > 0 ? (
            patients.map((patient) => (
              <SoftphonePatientRow key={patient.id} patient={patient} />
            ))
          ) : (
            <Typography sx={titleSx}>{who}</Typography>
          )}
          <Typography sx={captionSx}>{DESK_PHONE_HANDSET_HINT}</Typography>
        </>
      )}
      {deskPhoneDial.phase === "no_answer" && (
        <>
          <Typography sx={titleSx}>{DESK_PHONE_NO_ANSWER_TITLE}</Typography>
          <Typography sx={bodySx}>{who} hasn't been called yet.</Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              variant="outlined"
              size="small"
              onClick={moveDeskPhoneDialToComputer}
              sx={primaryActionSx}
            >
              {CALL_FROM_COMPUTER_LABEL}
            </Button>
            <Button
              size="small"
              onClick={dismissDeskPhoneDial}
              sx={e911CardTextButtonSx}
            >
              Dismiss
            </Button>
          </Stack>
        </>
      )}
      {deskPhoneDial.phase === "ended" && (
        <>
          <Typography sx={captionSx}>
            Call ended
            <Elapsed value={elapsed} />
          </Typography>
          <Typography sx={titleSx}>{who}</Typography>
          <Button
            size="small"
            onClick={dismissDeskPhoneDial}
            sx={{ ...e911CardTextButtonSx, alignSelf: "flex-start" }}
          >
            Dismiss
          </Button>
        </>
      )}
    </Stack>
  );
}
